import { useState, useEffect } from "react";
import { ArrowUpRight, ArrowDownLeft, ChevronLeft, ChevronRight } from "lucide-react";
import { getMiningWalletStatementAPI } from "../../api/miningApi";

export default function MiningWallet() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [walletType, setWalletType] = useState("token");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [userId, setUserId] = useState("");

  const formatAmount = (amount) =>
    Number.isInteger(amount) ? amount : parseFloat(amount.toFixed(5));

  useEffect(() => {
    fetchWalletData();
  }, [walletType, currentPage, pageSize, userId]);

  const fetchWalletData = async () => {
    try {
      setLoading(true);
      const response = await getMiningWalletStatementAPI(currentPage, pageSize, walletType, userId);
      if (response.status) {
        setTransactions(response.data.data);
        setTotalRecords(response.data.total_rows);
        setTotalPages(Math.ceil(response.data.total_rows / pageSize));
      }
    } catch (error) {
      console.error("Failed to fetch wallet statement:", error);
    } finally {
      setLoading(false);
    }
  };

  const renderPagination = () => {
    return Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
      if (
        page === 1 ||
        page === totalPages ||
        (page >= currentPage - 1 && page <= currentPage + 1)
      ) {
        return (
          <button
            key={page}
            onClick={() => setCurrentPage(page)}
            className={`h-8 w-8 rounded text-xs font-medium transition-colors ${
              page === currentPage
                ? "bg-ginox-green/20 border border-ginox-green/60 text-ginox-green"
                : "border border-border/70 bg-white/[0.02] text-muted-foreground hover:bg-white/[0.05] hover:text-foreground"
            }`}
          >
            {page}
          </button>
        );
      }
      if (page === currentPage - 2 || page === currentPage + 2) {
        return (
          <span key={page} className="h-8 w-8 flex items-center justify-center text-muted-foreground text-xs">
            ...
          </span>
        );
      }
      return null;
    });
  };

  const walletTabs = ["token", "energy", "bnb"];

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
          <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
          Mining
        </div>
        <h1 className="font-serif italic text-[45px] leading-[1.02] text-foreground">Wallet <span className="text-gradient-ginox">Statement</span></h1>
        <p className="text-sm text-muted-foreground">Monitor wallet transactions and balance</p>
      </div>

      {/* Wallet Type Tabs */}
      <div className="flex gap-2">
        {walletTabs.map((tab) => (
          <button
            key={tab}
            onClick={() => { setWalletType(tab); setCurrentPage(1); }}
            className={`rounded-md px-4 py-2 text-xs font-medium uppercase tracking-wide transition-colors ${
              walletType === tab
                ? "border border-ginox-green/60 bg-ginox-green/10 text-ginox-green"
                : "border border-border/70 bg-white/[0.02] text-muted-foreground hover:bg-white/[0.05] hover:text-foreground"
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="p-5">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
              User ID
            </label>
            <input
              type="text"
              placeholder="Enter User ID"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              className="w-36 rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
            />
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
              Page
            </label>
            <select
              value={currentPage.toString()}
              onChange={(e) => setCurrentPage(parseInt(e.target.value))}
              className="w-16 rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
            >
              {Array.from({ length: Math.min(totalPages, 50) }, (_, i) => {
                let pageNum;
                if (totalPages <= 50) {
                  pageNum = i + 1;
                } else {
                  const start = Math.max(1, currentPage - 25);
                  pageNum = start + i;
                  if (pageNum > totalPages) return null;
                }
                return <option key={pageNum} value={pageNum.toString()}>{pageNum}</option>;
              }).filter(Boolean)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
              Page Size
            </label>
            <select
              value={pageSize.toString()}
              onChange={(e) => { setPageSize(parseInt(e.target.value)); setCurrentPage(1); }}
              className="w-20 rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
            >
              <option value="10">10</option>
              <option value="15">15</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <div className="border-b border-border/70 px-5 py-3.5">
          <p className="text-sm font-medium text-foreground">
            Transaction History — {walletType.charAt(0).toUpperCase() + walletType.slice(1)}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">Complete record of all {walletType} wallet transactions</p>
        </div>
        <table className="w-full border-separate border-spacing-0">
          <thead>
            <tr>
              {["Date", "User ID", "Type", "Wallet Type", "Amount", "Balance", "Message"].map((h) => (
                <th key={h} className="border-b border-border/70 px-4 py-3 text-left text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="py-12 text-center text-sm text-muted-foreground">Loading...</td>
              </tr>
            ) : transactions.length > 0 ? (
              transactions.map((transaction) => (
                <tr key={transaction._id} className="group hover:bg-white/[0.02] transition-colors">
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">
                    {new Date(transaction.createdAt).toLocaleDateString("en-US", {
                      year: "numeric", month: "short", day: "numeric",
                    })}
                  </td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-muted-foreground font-mono">
                    {transaction.userId}
                  </td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm">
                    <div className="flex items-center gap-1.5">
                      {transaction.tranx_type === "Credit" ? (
                        <ArrowDownLeft className="w-3.5 h-3.5 text-emerald-400" />
                      ) : (
                        <ArrowUpRight className="w-3.5 h-3.5 text-red-400" />
                      )}
                      <span className="text-muted-foreground text-sm">{transaction.tranx_type}</span>
                    </div>
                  </td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-muted-foreground">
                    {transaction.wallet_type}
                  </td>
                  <td className={`border-b border-border/40 px-5 py-3.5 text-sm font-mono font-semibold ${
                    transaction.tranx_type === "Credit" ? "text-emerald-400" : "text-red-400"
                  }`}>
                    {transaction.tranx_type === "Credit" ? "+" : "-"}{formatAmount(transaction.amount)}
                  </td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">
                    {transaction.total.toFixed(0)}
                  </td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-muted-foreground max-w-xs truncate">
                    {transaction.message}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7} className="py-12 text-center text-sm text-muted-foreground">No transactions found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {(totalPages > 1 || loading) && (
        <div className="flex flex-col items-center gap-3 md:flex-row md:items-center md:justify-between">
          <p className="text-xs text-muted-foreground">
            {loading
              ? "Loading..."
              : `Showing ${(currentPage - 1) * pageSize + 1} to ${Math.min(currentPage * pageSize, totalRecords)} of ${totalRecords} transactions`}
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1 || loading}
              className="h-8 w-8 rounded border border-border/70 bg-white/[0.02] text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
            {!loading && renderPagination()}
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages || loading}
              className="h-8 w-8 rounded border border-border/70 bg-white/[0.02] text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
